import React, { useState } from 'react';
import { Alert, Modal, Spinner } from 'react-bootstrap';
import diseaseService from '../../../services/diseaseService';
import Icon from '../../common/Icons';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { parseDiseaseFile } from '../utils/parseDiseaseRows';

const PREVIEW_ROWS = 8;
const LIST_ROWS = 30;
const MAX_IMPORT_ROWS = 10000;

function Stat({ value, label, tone }) {
  return (
    <div className={`hd-dis-import__stat hd-dis-import__stat--${tone}`}>
      <span className="hd-dis-import__stat-value">{value}</span>
      <span className="hd-dis-import__stat-label">{label}</span>
    </div>
  );
}

export default function DiseaseImportModal({ show, onHide, onImported }) {
  const copy = HEAD_DOCTOR_COPY.DISEASES;
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFileName('');
    setError('');
    setPreview(null);
    setResult(null);
  };

  const close = () => {
    if (importing) return;
    onHide();
    reset();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    reset();
    setFileName(file.name);
    setParsing(true);
    try {
      const parsed = await parseDiseaseFile(file);
      let existingCodes = null;
      try {
        const res = await diseaseService.getDiseases({});
        if (res.success && Array.isArray(res.data)) {
          existingCodes = new Set(res.data.map((d) => String(d.code).toUpperCase()));
        }
      } catch {
        // Le backend rejette de toute façon les codes existants.
      }
      const existingRows = existingCodes ? parsed.items.filter((i) => existingCodes.has(i.code)) : [];
      const fresh = existingCodes ? parsed.items.filter((i) => !existingCodes.has(i.code)) : parsed.items;
      setPreview({ ...parsed, fresh, existingRows, checkedAgainstReferential: Boolean(existingCodes) });
    } catch (e) {
      setError(e.message || 'Lecture du fichier impossible.');
    } finally {
      setParsing(false);
    }
  };

  const confirm = async () => {
    if (!preview?.fresh.length) return;
    setImporting(true);
    setError('');
    try {
      const res = await diseaseService.importDiseases(preview.fresh);
      setResult(res.data);
      setPreview(null);
      await onImported?.(res.data);
    } catch (e) {
      setError(e.message || 'Import impossible.');
    } finally {
      setImporting(false);
    }
  };

  const rejectedCount = preview
    ? preview.existingRows.length + preview.duplicateRows.length + preview.invalid.length
    : 0;
  const tooMany = (preview?.fresh.length || 0) > MAX_IMPORT_ROWS;

  return (
    <Modal
      show={show}
      onHide={close}
      centered
      size="lg"
      backdrop={importing ? 'static' : true}
      dialogClassName="hd-dis-import-dialog"
    >
      <Modal.Header closeButton={!importing}>
        <Modal.Title as="h2" className="h5 mb-0">
          {copy.IMPORT_TITLE}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="hd-dis-import">
        {!result ? (
          <>
            <p className="hd-dis-import__hint">{copy.IMPORT_HINT}</p>

            <label className={`hd-dis-import__drop${parsing || importing ? ' is-disabled' : ''}`}>
              <Icon name="fileExcel" size={28} aria-hidden />
              <span className="hd-dis-import__drop-title">{fileName || copy.IMPORT_PICK}</span>
              <span className="hd-dis-import__drop-sub">.xlsx · .xls · .csv — {copy.IMPORT_MAX_LABEL}</span>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFile}
                disabled={parsing || importing}
                className="hd-dis-import__file"
              />
            </label>

            {parsing ? (
              <div className="hd-dis-import__loading">
                <Spinner animation="border" size="sm" /> Lecture du fichier…
              </div>
            ) : null}

            {error ? (
              <Alert variant="danger" className="mb-0">
                {error}
              </Alert>
            ) : null}

            {preview ? (
              <>
                <p className="hd-dis-import__file-meta">
                  Feuille « {preview.sheetName} »
                  {preview.hasHeader ? '' : ' · pas d’en-tête détecté : colonnes A et B'}
                </p>

                <div className="hd-dis-import__stats">
                  <Stat value={preview.fresh.length} label="À ajouter" tone="ok" />
                  <Stat value={preview.existingRows.length} label="Déjà existants" tone="warn" />
                  <Stat value={preview.duplicateRows.length} label="Doublons du fichier" tone="warn" />
                  <Stat value={preview.invalid.length} label="Invalides" tone="bad" />
                </div>

                {tooMany ? (
                  <Alert variant="danger" className="mb-0 py-2 small">
                    Le fichier dépasse {MAX_IMPORT_ROWS} nouvelles lignes : découpez-le en plusieurs fichiers.
                  </Alert>
                ) : null}

                {!preview.checkedAgainstReferential ? (
                  <Alert variant="warning" className="mb-0 py-2 small">
                    Référentiel non vérifié : les codes déjà existants seront rejetés à l’import.
                  </Alert>
                ) : null}

                {preview.fresh.length > 0 ? (
                  <div className="hd-dis-import__table-wrap">
                    <table className="table table-sm mb-0 hd-dis-import__table">
                      <thead>
                        <tr>
                          <th>{copy.COL_CODE}</th>
                          <th>{copy.COL_NAME}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.fresh.slice(0, PREVIEW_ROWS).map((item) => (
                          <tr key={item.code}>
                            <td>
                              <span className="hd-dis-code">{item.code}</span>
                            </td>
                            <td>{item.name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {preview.fresh.length > PREVIEW_ROWS ? (
                      <p className="hd-dis-import__more">… et {preview.fresh.length - PREVIEW_ROWS} autre(s)</p>
                    ) : null}
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0">
                    Aucun nouveau code à ajouter dans ce fichier.
                  </Alert>
                )}

                {rejectedCount > 0 ? (
                  <details className="hd-dis-import__details">
                    <summary>Voir les {rejectedCount} ligne(s) rejetée(s)</summary>
                    <ul>
                      {preview.existingRows.slice(0, LIST_ROWS).map((r) => (
                        <li key={`e-${r.code}`}>
                          <strong>{r.code}</strong> — code déjà existant
                        </li>
                      ))}
                      {preview.duplicateRows.slice(0, LIST_ROWS).map((r) => (
                        <li key={`d-${r.rowNumber}`}>
                          Ligne {r.rowNumber} : <strong>{r.code}</strong> — doublon dans le fichier
                        </li>
                      ))}
                      {preview.invalid.slice(0, LIST_ROWS).map((r) => (
                        <li key={`i-${r.rowNumber}`}>
                          Ligne {r.rowNumber} : {r.code} — {r.reasons.join(', ')}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </>
            ) : null}
          </>
        ) : (
          <>
            <Alert variant={result.created > 0 ? 'success' : 'info'} className="mb-0">
              Import terminé : <strong>{result.created}</strong> maladie(s) ajoutée(s).
            </Alert>
            <div className="hd-dis-import__stats">
              <Stat value={result.created} label="Ajoutées" tone="ok" />
              <Stat value={result.rejected_duplicates} label="Doublons rejetés" tone="warn" />
              <Stat value={result.invalid} label="Invalides" tone="bad" />
            </div>
            {result.duplicates?.length > 0 ? (
              <details className="hd-dis-import__details">
                <summary>Codes rejetés</summary>
                <ul>
                  {result.duplicates.map((d) => (
                    <li key={`${d.row}-${d.code}`}>
                      <strong>{d.code}</strong> — {d.reason}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" onClick={close} disabled={importing}>
          {result ? 'Fermer' : copy.BTN_CANCEL}
        </button>
        {!result ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={confirm}
            disabled={importing || parsing || tooMany || !preview?.fresh.length}
          >
            {importing ? copy.IMPORT_RUNNING : `${copy.IMPORT_CONFIRM} (${preview?.fresh.length || 0})`}
          </button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
